import React, {PropsWithChildren} from "react";
import {Tabs, Tab, TabContentProps} from "react-bootstrap";
import {useNavigate, useParams} from "react-router-dom";

/**
 * URL-driven Bootstrap tabs.
 *
 * The active tab is read from the React Router param named `queryParameter`.
 * Selecting a tab navigates to a new URL and (optionally) calls `onSelect`.
 *
 * `baseUrl`:
 *   - When a string, the next URL is `${baseUrl}/${key}`. A trailing slash on
 *     `baseUrl` is stripped to avoid producing `//key`.
 *   - When a function, it is called with the selected key and must return the
 *     full target URL (useful for routes that interleave the tab key, like
 *     `/admin/courses/:id/:tab`).
 *
 * If the route param is missing, `defaultActiveKey` is used as the active tab.
 * Selecting a falsy key is a no-op (Bootstrap can emit `null` when no tab
 * matches), and `onSelect` only fires after a successful navigation.
 */
function UrlTabs({
  queryParameter, defaultActiveKey, baseUrl, onSelect, children
}: PropsWithChildren<{
  queryParameter: string;
  baseUrl: string | ((key: string) => string);
  onSelect?: (key: string) => void;
  defaultActiveKey?: string;
}>) {
  const params = useParams();
  const navigate = useNavigate();
  const activeKey = params[queryParameter] || defaultActiveKey;
  return (
    <Tabs
      onSelect={(key) => {
        if (key) {
          const url = typeof baseUrl === 'string'
            ? `${baseUrl.replace(/\/$/, '')}/${key}`
            : baseUrl(key);

          navigate(url);
          if (onSelect) onSelect(key);
        }
      }}
      activeKey={activeKey}
    >
      {children}
    </Tabs>
  )
}

UrlTabs.Tab = Tab;
UrlTabs.TabContent = function ContentWrapper ({ children }: TabContentProps) {
  return <Tab.Content className="py-3">{children}</Tab.Content>
}

export default UrlTabs;
